import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import DrawerLayout from "../../../components/layout/DrawerLayout";
import { Stack } from "@mui/material";
import { useSnackbar } from "notistack";
import { isSubAdmin } from '../../../utils/StringUtil';
import {
  SNACKBAR_ERROR,
  SNACKBAR_SUCCESS,
  SNACKBAR_WARNING,
  RECORD_DISPLAY_LIMIT
} from "../../../utils/constants/constants";
import {
  apiGetDealBookings,
  apiSaveDealBooking,
  getOpportunity
} from "../../../utils/api/api";
import DealBookingForm from "./DealBookingForm";
import DealBookingHistory from "./DealBookingHistory";

/**
 * DealBookingDrawer Component
 * 
 * A drawer interface for creating and managing deal bookings for investment schemes.
 * handles the workflow between scheme selection, booking form, and history display.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls whether the drawer is open
 * @param {Function} props.toggleDrawer - Function to toggle the drawer state
 * @param {Object} props.scheme 
 */
const DealBookingDrawer = ({ open, toggleDrawer, scheme }) => {

  // State for scheme selection
  const [schemeList, setSchemeList] = useState([]);
  const [oppRid, setOppRid] = useState(null);
  const [schemeName, setSchemeName] = useState("");
  const [companyLegalName, setCompanyLegalName] = useState("");

  // State for form management
  const [postingDate, setPostingDate] = useState(new Date());
  const [purchaseQuantity, setPurchaseQuantity] = useState("");
  const [purchaseRate, setPurchaseRate] = useState("");
  const [unitType, setUnitType] = useState("");
  const [isFormDirty, setIsFormDirty] = useState(false);

  // State for validation
  const [purchaseQuantityError, setPurchaseQuantityError] = useState(false);
  const [purchaseRateError, setPurchaseRateError] = useState(false);
  const [unitTypeError, setUnitTypeError] = useState(false);

  // State for deal booking history
  const [dealBookingList, setDealBookingList] = useState([]);
  const [latestBookingData, setLatestBookingData] = useState(null);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  // State for loading indicators
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Track active requests to prevent duplicates
  const activeRequestRef = useRef(null);
  const isInitialMountRef = useRef(true);

  const { enqueueSnackbar } = useSnackbar();

  /**
   * Fetches the list of available schemes
   * @param {Object} param - Query parameters for the API
   */
  const getSchemeList = (param = {}) => {
    param.pageNumber = 0;
    getOpportunity(param)
      .then((data) => {
        setSchemeList(data.opportunityList);
      })
      .catch((error) => {
        enqueueSnackbar(error, SNACKBAR_ERROR);
      });
  };

  /**
   * Handles closing the drawer
   * Prevents default behavior and resets form
   */
  const handleClose = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    resetForm();
    toggleDrawer();
  };

  /**
   * Handles pagination changes
   * @param {Event} _event - Event object (unused)
   * @param {number} value - New page number
   */
  const handlePageChange = (_event, value) => {
    if (value !== page) {
      setPage(value);
    }
  };

  /**
   * Populates form fields with data from the latest booking
   * @param {Object} latestBooking - Latest booking data object
   */
  const populateFormWithLatestBooking = (latestBooking) => {
    if (!latestBooking) return;

    if (latestBooking.postingDate) {
      try {
        const parts = latestBooking.postingDate.split('-');
        if (parts.length === 3) {
          const year = parseInt(parts[0]);
          const month = parseInt(parts[1]) - 1;
          const day = parseInt(parts[2]);
          const date = new Date(year, month, day);
          setPostingDate(date);
        } else {
          setPostingDate(new Date());
        }
      } catch (error) {
        setPostingDate(new Date());
      }
    }

    if (latestBooking.purchaseQuantity !== null && latestBooking.purchaseQuantity !== undefined) {
      setPurchaseQuantity(latestBooking.purchaseQuantity.toString());
    }

    if (latestBooking.purchaseRate !== null && latestBooking.purchaseRate !== undefined) {
      setPurchaseRate(latestBooking.purchaseRate.toString());
    }

    if (latestBooking.unitType) {
      setUnitType(latestBooking.unitType);
    }
  };

  /**
   * Fetches the list of deal bookings for the selected scheme
   * with duplicate prevention using request signatures
   */
  const getDealBookingList = useCallback(() => {
    if (!oppRid) return;

    const requestSignature = JSON.stringify({ oppRid, page });
    if (activeRequestRef.current === requestSignature) {
      return;
    }

    activeRequestRef.current = requestSignature;
    setIsLoading(true);

    apiGetDealBookings({
      oppRid,
      page: page,
      limit: RECORD_DISPLAY_LIMIT
    })
      .then((response) => {
        activeRequestRef.current = null;
        setIsLoading(false);
        const data = response.data || response;
        const bookings = data.dealBookingList || [];
        setDealBookingList(bookings);

        let newTotalRecords;
        if (data.records !== undefined) {
          newTotalRecords = data.records;
        } else if (response.records !== undefined) {
          newTotalRecords = response.records;
        } else if (response.totalRecords !== undefined) {
          newTotalRecords = response.totalRecords;
        } else if (data.totalRecords !== undefined) {
          newTotalRecords = data.totalRecords;
        } else if (response.total !== undefined) {
          newTotalRecords = response.total;
        } else if (data.total !== undefined) {
          newTotalRecords = data.total;
        } else {
          if (bookings.length === 0 && page === 1) {
            newTotalRecords = 0;
          } else if (bookings.length < RECORD_DISPLAY_LIMIT) {
            newTotalRecords = (page - 1) * RECORD_DISPLAY_LIMIT + bookings.length;
          } else {
            const confirmedMinimum = page * RECORD_DISPLAY_LIMIT;
            if (page === 1) {
              newTotalRecords = Math.max(RECORD_DISPLAY_LIMIT * 2, confirmedMinimum);
            } else {
              newTotalRecords = Math.max(confirmedMinimum + RECORD_DISPLAY_LIMIT, totalRecords);
            }
          }
        }

        if (newTotalRecords !== totalRecords) {
          setTotalRecords(newTotalRecords);
        }

        if (page > 1 && bookings.length === 0) {
          if (newTotalRecords === 0) {
            setPage(1);
          } else {
            const lastValidPage = Math.max(1, Math.ceil(newTotalRecords / RECORD_DISPLAY_LIMIT));
            if (page > lastValidPage) {
              setPage(lastValidPage);
            }
          }
        }

        if (data.latestDealBooking) {
          setLatestBookingData(data.latestDealBooking);
          populateFormWithLatestBooking(data.latestDealBooking);
        }

        setIsFormDirty(false);
      })
      .catch((error) => {
        activeRequestRef.current = null;
        setIsLoading(false);
        enqueueSnackbar(error || "Failed to fetch deal bookings", SNACKBAR_ERROR);
        setDealBookingList([]);
      });
  }, [oppRid, page, totalRecords, enqueueSnackbar]);
  useEffect(() => {
    if (open && isInitialMountRef.current) {
      setPage(1);
      if (scheme) {
        setOppRid(scheme.oppRid);
        setSchemeName(scheme.oppTitle || "");
        setCompanyLegalName(scheme.legalNameOfEntity || "");
      } else {
        setOppRid(null);
        setSchemeName("");
        setCompanyLegalName("");
        getSchemeList();
      }
      isInitialMountRef.current = false;
    }
  }, [open, scheme]);

  useEffect(() => {
    if (open && oppRid) {
      getDealBookingList();
    }
  }, [open, oppRid, page]);

  /**
   * Resets all form fields and state
   */
  const resetForm = () => {
    setPostingDate(new Date());
    setPurchaseQuantity("");
    setPurchaseRate("");
    setUnitType("");
    setPurchaseQuantityError(false);
    setPurchaseRateError(false);
    setUnitTypeError(false);
    setLatestBookingData(null);
    setOppRid(null);
    setSchemeList([]);
    setDealBookingList([]);
    setSchemeName("");
    setCompanyLegalName("");
    setPage(1);
    setTotalRecords(0);
    setIsFormDirty(false);

    activeRequestRef.current = null;
    isInitialMountRef.current = true;
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (oppRid) {
      setPostingDate(new Date());
      setPurchaseQuantity("");
      setPurchaseRate("");
      setUnitType("");
      setPurchaseQuantityError(false);
      setPurchaseRateError(false);
      setUnitTypeError(false);
      setLatestBookingData(null);
      setPage(1);
      activeRequestRef.current = null;
    }
  }, [oppRid]);

  /**
   * Validates form fields
   * @returns {boolean} Whether the form is valid
   */
  const validateForm = () => {
    let isValid = true;
    if (!purchaseQuantity || isNaN(Number(purchaseQuantity)) || Number(purchaseQuantity) <= 0) {
      setPurchaseQuantityError(true);
      isValid = false;
    }
    if (!purchaseRate || isNaN(Number(purchaseRate)) || Number(purchaseRate) <= 0) {
      setPurchaseRateError(true);
      isValid = false;
    }
    if (!unitType || unitType.trim() === "") {
      setUnitTypeError(true);
      isValid = false;
    }
    return isValid;
  };

  /**
   * Formats a Date object to YYYY-MM-DD string for API submission
   * @param {Date} date - Date to format
   * @returns {string} Formatted date string
   */
  const formatDateForApi = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  /**
   * Handles saving a new deal booking
   */
  const handleSaveBooking = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!oppRid) {
      enqueueSnackbar("Please select a scheme", SNACKBAR_WARNING);
      return;
    }
    if (!validateForm()) {
      enqueueSnackbar("Please fill in all required fields correctly", SNACKBAR_WARNING);
      return;
    }

    setIsSaving(true);
    const requestBody = {
      oppRid: oppRid,
      postingDate: formatDateForApi(postingDate),
      purchaseQuantity: Number(purchaseQuantity),
      purchaseRate: Number(purchaseRate),
      unitType: unitType
    };

    apiSaveDealBooking(requestBody)
      .then(() => {
        setIsSaving(false);
        enqueueSnackbar("Deal booking created successfully", SNACKBAR_SUCCESS);
        setPostingDate(new Date());
        setPurchaseQuantity("");
        setPurchaseRate("");
        setUnitType("");
        setPage(1);
        setIsFormDirty(false);
        activeRequestRef.current = null;
        getDealBookingList();
      })
      .catch((error) => {
        setIsSaving(false);
        enqueueSnackbar(
          error || "An error occurred while saving the deal booking",
          SNACKBAR_ERROR
        );
      });
  };

  /**
   * Updates a form field value
   * @param {string} field - Field name
   * @param {any} value - New field value
   */
  const updateFormField = (field, value) => {
    switch (field) {
      case 'postingDate':
        setPostingDate(value);
        break;
      case 'purchaseQuantity':
        setPurchaseQuantity(value);
        setPurchaseQuantityError(false);
        break;
      case 'purchaseRate':
        setPurchaseRate(value);
        setPurchaseRateError(false);
        break;
      case 'unitType':
        setUnitType(value);
        setUnitTypeError(false);
        break;
      default:
        return;
    }
    setIsFormDirty(true);
  };

  return (
    <DrawerLayout
      open={open}
      title={scheme ? "Deal Booking" : ""}
      subtitle={scheme ? scheme.oppTitle : ""}
      closeAction={handleClose}
      drawerWidth={"50%"}
      positiveAction={
        { label: 'Close', onClick: handleClose }
      }
    >
      {!isSubAdmin() && (
        <Stack sx={{ marginBottom: "2rem" }} direction="column" spacing={2}>
          <DealBookingForm
            schemeName={schemeName}
            companyLegalName={companyLegalName}
            postingDate={postingDate}
            purchaseQuantity={purchaseQuantity}
            purchaseRate={purchaseRate}
            unitType={unitType}
            purchaseQuantityError={purchaseQuantityError}
            purchaseRateError={purchaseRateError}
            unitTypeError={unitTypeError}
            isLoading={isLoading}
            isSaving={isSaving}
            isFormDirty={isFormDirty}
            onFieldChange={updateFormField}
            onSave={handleSaveBooking}
          />
        </Stack>
      )}

      <DealBookingHistory
        oppRid={oppRid}
        dealBookingList={dealBookingList}
        isLoading={isLoading}
        page={page}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
      />
    </DrawerLayout>
  );
};

DealBookingDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  scheme: PropTypes.object
};

export default DealBookingDrawer;








